import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowUpRight, Award, Heart, Loader2, ShoppingBag } from 'lucide-react';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import CheckoutConsentModal from '@/components/CheckoutConsentModal';
import { HERITAGE_ESTATE, HERITAGE_BUNDLES } from '@/data/catalog';
import useWishlist from '@/hooks/useWishlist';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CATEGORIES = ['All', 'Fire Starters', 'Survival Gear'];

export default function HeritageEstatePage() {
  const [filter, setFilter] = useState('All');
  const [busySlug, setBusySlug] = useState(null);
  const [buyError, setBuyError] = useState(null);
  const [consentProduct, setConsentProduct] = useState(null);
  const { isSaved, toggle } = useWishlist();

  const items = useMemo(() => {
    if (filter === 'All') return HERITAGE_ESTATE;
    return HERITAGE_ESTATE.filter((p) => p.category === filter);
  }, [filter]);

  const flagship = HERITAGE_ESTATE.filter((p) => p.featured);

  const openConsent = (product) => {
    setBuyError(null);
    setConsentProduct(product);
  };

  const confirmAndCheckout = async () => {
    if (!consentProduct) return;
    setBusySlug(consentProduct.slug);
    try {
      const res = await axios.post(`${API}/checkout/session`, {
        slug: consentProduct.slug,
        origin_url: window.location.origin,
      });
      if (res.data?.url) {
        window.location.href = res.data.url;
      } else {
        throw new Error('No checkout URL returned');
      }
    } catch (err) {
      setBuyError(err?.response?.data?.detail || 'Could not start checkout. Please try again.');
      setBusySlug(null);
      setConsentProduct(null);
    }
  };

  return (
    <div className="App">
      <Navigation />
      <main data-testid="heritage-estate-page" className="pt-44">
        {/* Hero band */}
        <section className="relative bg-[var(--brand-forest-deep)] text-[var(--brand-parchment)] overflow-hidden">
          <div className="absolute inset-0 grain opacity-40 pointer-events-none" />
          <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-28">
            <span className="font-eyebrow !text-[var(--brand-gold)]">
              The Collection · Heritage Estate
            </span>
            <h1
              data-testid="heritage-estate-hero-title"
              className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight max-w-3xl"
            >
              Built for the hill,<br />
              <span className="italic text-[var(--brand-gold)]">not the Instagram.</span>
            </h1>
            <p className="mt-6 max-w-2xl text-base sm:text-lg opacity-85 leading-relaxed">
              Eleven honest tools stamped with our Heritage Estate mark. Fire starters
              and survival kit chosen for real Scottish weather &mdash; the kind that
              soaks your gloves and blows out the first two matches before you&rsquo;ve
              got the tinder set. Kit that lasts a season of hard use and quietly
              earns its keep.
            </p>
            <p className="mt-4 text-xs uppercase tracking-[0.28em] opacity-70">
              Heritage Estate by Eco-Bikes&nbsp;&amp; Camping Supplies Co.
            </p>
          </div>
        </section>

        {/* Flagship callout */}
        {flagship.length > 0 && (
          <section className="bg-[var(--brand-parchment)] py-12 border-b border-[var(--brand-stone)]">
            <div className="max-w-7xl mx-auto px-6 lg:px-10">
              <div className="flex items-center gap-3 mb-6">
                <Award size={18} className="text-[var(--brand-gold)]" strokeWidth={1.8} />
                <span className="font-eyebrow">The Flagship Two</span>
              </div>
              <p className="text-[var(--brand-muted)] text-base leading-relaxed max-w-2xl">
                Two kits the Gamekeeper reaches for first. Waxed jute for the wet weather,
                hemp tinder for the long burn. Everything else in the collection follows
                the same standard.
              </p>
            </div>
          </section>
        )}

        {/* Curated bundle band */}
        {HERITAGE_BUNDLES.length > 0 && (
          <section
            data-testid="heritage-estate-bundle-band"
            className="bg-[var(--brand-parchment-warm)] py-14 border-b border-[var(--brand-stone)]"
          >
            <div className="max-w-7xl mx-auto px-6 lg:px-10">
              <div className="flex items-end justify-between gap-6 mb-6 flex-wrap">
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <ShoppingBag size={16} className="text-[var(--brand-gold)]" strokeWidth={1.8} />
                    <span className="font-eyebrow">
                      {HERITAGE_BUNDLES.length === 1 ? 'Curated Bundle' : `Curated Bundles · ${HERITAGE_BUNDLES.length}`}
                    </span>
                  </div>
                  <h2 className="font-display text-2xl sm:text-3xl text-[var(--brand-forest)] tracking-tight leading-tight">
                    Kit chosen to go out together.
                  </h2>
                </div>
                <p className="text-xs text-[var(--brand-muted)] max-w-sm">
                  Buy the whole kit and save on the individual prices.
                  Fulfilment ships every SKU in one parcel.
                </p>
              </div>
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                {HERITAGE_BUNDLES.map((b) => {
                  const bundleSaved = isSaved(b.slug);
                  return (
                  <article
                    key={b.slug}
                    data-testid={`heritage-estate-bundle-card-${b.slug}`}
                    className="flex flex-col bg-[var(--brand-parchment)] border border-[var(--brand-stone)] overflow-hidden hover:border-[var(--brand-gold)] transition-colors"
                  >
                    <div className="relative aspect-[4/3] overflow-hidden bg-[var(--brand-parchment-warm)]">
                      <img
                        src={b.image}
                        alt={b.name}
                        className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 hover:scale-105"
                      />
                      <span className="absolute top-3 left-3 bg-[var(--brand-forest)] text-[var(--brand-parchment)] text-[10px] tracking-[0.22em] uppercase px-2.5 py-1">
                        {b.badge}
                      </span>
                      <span className="absolute top-3 right-3 bg-[var(--brand-gold)] text-[var(--brand-forest-deep)] text-[10px] tracking-[0.22em] uppercase px-2.5 py-1 font-bold">
                        {b.saving.replace('Save ', '').replace(/\s*\(.+\)/, '')}
                      </span>
                      <button
                        type="button"
                        data-testid={`wishlist-toggle-${b.slug}`}
                        aria-label={bundleSaved ? `Remove ${b.name} from saved items` : `Save ${b.name}`}
                        aria-pressed={bundleSaved}
                        onClick={() => toggle({ slug: b.slug, name: b.name, price: b.price, image: b.image })}
                        className={
                          'absolute bottom-3 right-3 w-9 h-9 grid place-items-center bg-[var(--brand-parchment)]/90 border border-[var(--brand-stone)] hover:border-[var(--brand-gold)] transition-colors ' +
                          (bundleSaved ? 'text-[var(--brand-gold)]' : 'text-[var(--brand-forest)]')
                        }
                      >
                        <Heart size={14} className={bundleSaved ? 'fill-[var(--brand-gold)]' : ''} />
                      </button>
                    </div>
                    <div className="p-5 flex flex-col grow">
                      <div className="text-[10px] tracking-[0.24em] uppercase text-[var(--brand-muted)] font-semibold">
                        {b.sku}
                      </div>
                      <h3 className="mt-1.5 font-display text-xl text-[var(--brand-forest)] tracking-tight leading-tight">
                        <Link
                          to={`/product/${b.slug}`}
                          data-testid={`heritage-estate-bundle-title-${b.slug}`}
                          className="hover:text-[var(--brand-olive)]"
                        >
                          {b.name}
                        </Link>
                      </h3>
                      <p className="mt-1 text-sm italic text-[var(--brand-muted)]">
                        {b.subtitle}
                      </p>
                      <ul className="mt-3 space-y-1 text-xs text-[var(--brand-muted)] grow">
                        {b.includes.map((it) => (
                          <li key={it.slug} className="flex items-center gap-2">
                            <span className="w-1 h-1 bg-[var(--brand-gold)]" />
                            {it.name}
                          </li>
                        ))}
                      </ul>
                      <div className="mt-4 flex items-baseline gap-2">
                        <span className="font-display text-2xl font-semibold text-[var(--brand-olive)]">
                          {b.price}
                        </span>
                        <span className="text-xs text-[var(--brand-muted)] line-through">
                          {b.rrp}
                        </span>
                      </div>
                      <div className="mt-4 pt-4 border-t border-[var(--brand-stone)] grid grid-cols-2 gap-2">
                        <Link
                          to={`/product/${b.slug}`}
                          data-testid={`heritage-estate-bundle-details-${b.slug}`}
                          className="min-h-[40px] border border-[var(--brand-forest)] text-[var(--brand-forest)] text-xs font-semibold inline-flex items-center justify-center hover:bg-[var(--brand-forest)] hover:text-[var(--brand-parchment)] transition-colors"
                        >
                          Details
                        </Link>
                        <button
                          onClick={() => openConsent(b)}
                          disabled={busySlug === b.slug}
                          data-testid={`heritage-estate-bundle-buy-${b.slug}`}
                          className="btn-gold min-h-[40px] text-xs font-semibold inline-flex items-center justify-center gap-1.5 disabled:opacity-60 disabled:cursor-wait"
                        >
                          {busySlug === b.slug ? (
                            <>
                              <Loader2 size={12} className="animate-spin" /> Starting…
                            </>
                          ) : (
                            <>
                              <ShoppingBag size={12} /> Buy
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  </article>
                  );
                })}
              </div>
            </div>
          </section>
        )}

        {/* Filter chips */}
        <section className="bg-[var(--brand-parchment)] pt-10">
          <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <div
              data-testid="heritage-estate-filter"
              role="tablist"
              aria-label="Filter Heritage Estate products"
              className="flex flex-wrap gap-3"
            >
              {CATEGORIES.map((c) => {
                const count = c === 'All'
                  ? HERITAGE_ESTATE.length
                  : HERITAGE_ESTATE.filter((p) => p.category === c).length;
                const active = filter === c;
                return (
                  <button
                    key={c}
                    role="tab"
                    aria-selected={active}
                    data-testid={`heritage-estate-filter-${c.toLowerCase().replace(/\s+/g, '-')}`}
                    onClick={() => setFilter(c)}
                    className={
                      'px-4 py-2 text-xs font-semibold uppercase tracking-[0.2em] border transition-colors ' +
                      (active
                        ? 'bg-[var(--brand-forest)] text-[var(--brand-parchment)] border-[var(--brand-forest)]'
                        : 'bg-transparent text-[var(--brand-forest)] border-[var(--brand-stone)] hover:border-[var(--brand-gold)]')
                    }
                  >
                    {c} <span className="opacity-70">({count})</span>
                  </button>
                );
              })}
            </div>
          </div>
        </section>

        {/* Product grid */}
        <section className="bg-[var(--brand-parchment)] py-14">
          <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <div
              data-testid="heritage-estate-grid"
              className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6"
            >
              {items.map((p) => {
                const saved = isSaved(p.slug);
                return (
                  <article
                    key={p.slug}
                    data-testid={`heritage-estate-card-${p.slug}`}
                    className={
                      'product-card flex flex-col ' +
                      (p.featured ? 'ring-1 ring-[var(--brand-gold)]' : '')
                    }
                  >
                    <div className="relative aspect-[4/3] overflow-hidden">
                      <img
                        src={p.image}
                        alt={p.name}
                        className="absolute inset-0 w-full h-full object-cover editorial-img transition-transform duration-700 hover:scale-105"
                      />
                      {p.badge && (
                        <span className="absolute top-3 left-3 bg-[var(--brand-forest)] text-[var(--brand-parchment)] text-[10px] tracking-[0.22em] uppercase px-2.5 py-1">
                          {p.badge}
                        </span>
                      )}
                      <button
                        type="button"
                        data-testid={`wishlist-toggle-${p.slug}`}
                        aria-label={saved ? `Remove ${p.name} from saved items` : `Save ${p.name}`}
                        aria-pressed={saved}
                        onClick={() => toggle({ slug: p.slug, name: p.name, price: p.price, image: p.image })}
                        className={
                          'absolute top-3 right-3 w-9 h-9 grid place-items-center bg-[var(--brand-parchment)]/90 border border-[var(--brand-stone)] hover:border-[var(--brand-gold)] transition-all ' +
                          (saved ? 'text-[var(--brand-gold)]' : 'text-[var(--brand-forest)]')
                        }
                      >
                        <Heart size={16} className={saved ? 'fill-[var(--brand-gold)]' : ''} />
                      </button>
                      <span className="absolute bottom-3 left-3 bg-[var(--brand-parchment)]/90 text-[10px] tracking-[0.2em] uppercase px-2 py-1 font-semibold text-[var(--brand-forest)]">
                        {p.sku}
                      </span>
                    </div>
                    <div className="p-6 flex flex-col grow">
                      <div className="flex items-baseline justify-between gap-3">
                        <h3 className="font-display text-xl text-[var(--brand-forest)] tracking-tight leading-tight">
                          <Link
                            to={`/product/${p.slug}`}
                            data-testid={`heritage-estate-title-link-${p.slug}`}
                            className="hover:text-[var(--brand-olive)] transition-colors"
                          >
                            {p.name}
                          </Link>
                        </h3>
                        <span className="text-sm font-semibold text-[var(--brand-olive)] whitespace-nowrap">
                          {p.price}
                        </span>
                      </div>
                      <p className="mt-1 text-sm text-[var(--brand-muted)] italic">
                        {p.subtitle}
                      </p>
                      <p className="mt-3 text-sm text-[var(--brand-ink)] leading-relaxed grow">
                        {p.summary || p.blurb}
                      </p>

                      <ul className="mt-4 grid grid-cols-2 gap-x-3 gap-y-1.5 text-xs text-[var(--brand-muted)]">
                        {p.spec.map((s) => (
                          <li key={s} className="flex items-center gap-2">
                            <span className="w-1 h-1 bg-[var(--brand-gold)]" />
                            {s}
                          </li>
                        ))}
                      </ul>

                      <div className="mt-6 pt-5 border-t border-[var(--brand-stone)] grid grid-cols-2 gap-3">
                        <Link
                          to={`/product/${p.slug}`}
                          data-testid={`heritage-estate-details-${p.slug}`}
                          className="min-h-[44px] border border-[var(--brand-forest)] text-[var(--brand-forest)] text-sm font-semibold inline-flex items-center justify-center gap-1.5 hover:bg-[var(--brand-forest)] hover:text-[var(--brand-parchment)] transition-colors"
                        >
                          Details
                          <ArrowUpRight size={14} />
                        </Link>
                        <button
                          data-testid={`heritage-estate-buy-${p.slug}`}
                          onClick={() => openConsent(p)}
                          disabled={busySlug === p.slug}
                          className="btn-gold min-h-[44px] px-4 text-sm font-semibold tracking-wide inline-flex items-center justify-center gap-1.5 disabled:opacity-60 disabled:cursor-wait"
                        >
                          {busySlug === p.slug ? (
                            <>
                              <Loader2 size={13} className="animate-spin" /> Starting…
                            </>
                          ) : (
                            <>
                              <ShoppingBag size={13} /> Buy now
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>

            {buyError && (
              <p
                data-testid="heritage-estate-buy-error"
                className="mt-6 text-sm text-red-700 bg-red-50 border border-red-200 px-4 py-3 max-w-xl"
              >
                {buyError}
              </p>
            )}
          </div>
        </section>

        <CheckoutConsentModal
          product={consentProduct}
          busy={!!busySlug}
          onCancel={() => setConsentProduct(null)}
          onConfirm={confirmAndCheckout}
        />
      </main>
      <Footer />
    </div>
  );
}
